import { createContext, useContext, useEffect, useState } from 'react';
import { error, success } from '../../services/swal-mixin';
import { hideLoader, showLoader } from '../../utils/loader';
import { customBaseUrl, httpGet, httpPost } from '../http';

// Onboarding Steps
import Step3Img from '../../assets/images/wayabank_steps/fund.png';
import Step1Img from '../../assets/images/wayabank_steps/register.png';
import Step2Img from '../../assets/images/wayabank_steps/verify.png';
import Step4Img from '../../assets/images/wayabank_steps/dashboard.png';

// Section Images
import PersonalImg from '../../assets/images/personal.png';
import BusinessImg from '../../assets/images/waya_business_banking_v2.png';
import HeroMockup from '../../assets/images/wayabank_hero_mockup.png';
import WayaGramIcon from '../../assets/images/wayagramIcon.png';
import WayaPayIcon from '../../assets/images/wayaPayIcon.png';
import WayaPosIcon from '../../assets/images/wayaPosIcon.png';

// Legal/Partner Logos
import CbnLogo from '../../assets/images/legal/cbn.png';
import InterswitchLogo from '../../assets/images/legal/Interswitch_logo.svg.png';
import MastercardLogo from '../../assets/images/legal/mastercard-react-svg.svg';
import NdicLogo from '../../assets/images/legal/ndic.png';
import NibbsLogo from '../../assets/images/legal/nibbs_logo.png';
import VerveLogo from '../../assets/images/legal/Verve_Image.png';
import VisaLogo from '../../assets/images/legal/Visa_Inc._logo_(2021–present).svg.png';

export const CmsContext = createContext();

export const useCms = () => useContext(CmsContext);
const fallbacks = {
  settings: {
    site_name: 'WayaBank',
    contact_email: 'support@wayapaychat.com',
    contact_phone: '+234 1 234 5678',
    cbn_logo: CbnLogo,
    ndic_logo: NdicLogo,
    app_store_url: 'https://apps.apple.com/ng/app/wayabank/id123456789',
    play_store_url: 'https://play.google.com/store/apps/details?id=com.wayabank.app',
    footer_description: 'Easy, Fast and Safe digital banking for Nigeria',
    mission:
      'To tear down financial barriers and build a completely inclusive, tech-driven ecosystem that empowers every Nigerian individual and business to thrive, scale, and achieve true financial independence.',
    b2b_documentation: '',
  },
  aboutContent: [
    {
      id: 'f1',
      title: 'For the Hustlers and the Builders',
      content:
        'Small and medium-sized enterprises are the lifeblood of the Nigerian economy. We provide the tools to make your business unstoppable.',
      image_url:
        'https://images.unsplash.com/photo-1556742044-3c52d6e88c62?auto=format&fit=crop&w=800&q=80',
      tag: 'For SMEs & Businesses',
    },
    {
      id: 'f2',
      title: 'For Every Nigerian',
      content:
        'Your money should work for you, on your terms. We power millions of individuals with an intuitive digital banking experience.',
      image_url:
        'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80',
      tag: 'For Individuals',
    },
  ],
  aboutIntro: {
    pill: 'About Us',
    title: 'Banking is Just the Beginning. <br class="d-none d-lg-block" /> We Are the Engine of Your Growth.',
    content: 'At WayaBank, we believe that access to seamless financial services is a right, not a privilege. Today, we are more than just a bank—we are a world-class technology engine driving unstoppable growth for thousand of entrepreneurs and millions of individuals across Nigeria.',
    mission_title: 'Our Mission',
    advantages_title: 'The WayaBank Advantage: Why We Stand Out',
  },
  homeContent: {
    hero_headline: 'Waya your money, your way.',
    hero_subtext:
      'Designed by banking experts. WayaBank gives you the guidance, data and innovation you need to become a better transactor.',
    hero_image: HeroMockup,
    pill_text: 'We are licensed by CBN and insured by NDIC',
    app_store_url: 'https://apps.apple.com/ng/app/wayabank/id123456789',
    play_store_url: 'https://play.google.com/store/apps/details?id=com.wayabank.app',
    stats: [
      { value: '250K+', label: 'Active users' },
      { value: '₦28B+', label: 'Processed' },
      { value: '99.9%', label: 'Uptime' },
    ],
  },
  bankingDetails: {
    personal: {
      title: 'Digital Personal Banking',
      subtitle: 'For Individuals',
      description:
        'WayaBank provides completely digital personal banking via web, mobile app and USSD.',
      image_url: PersonalImg,
      bullets: [
        'Send money using just a phone number or email',
        'Receive transfers from any Nigerian bank',
        'Pay Airtime, Data, Utility & Betting bills',
        'Withdraw to your other bank accounts',
        'Free debit card (virtual & physical)',
      ],
    },
    business: {
      title: 'Digital Business Banking',
      subtitle: 'For Businesses',
      description:
        'Tailor-made for businesses, WayaBank grants businesses an ability to accept payments and carry out bulk transfers.',
      image_url: BusinessImg,
      bullets: [
        'Accept payments from customers via phone number',
        'Bulk payments to staff and vendors',
        'Dedicated business account with NUBAN',
        'Real-time transaction notifications',
      ],
      stats: [
        { value: '250K+', label: 'Customers' },
        { value: '₦2B+', label: 'Processed' },
      ],
    },
  },
  advantages: [
    {
      title: 'Built on Superior Technology',
      desc: 'Infrastructure built for 99.9% uptime, ensuring your transactions go through instantly—every single time.',
    },
    {
      title: 'Unlocking Access to Credit',
      desc: 'Fast, fair access to the credit you deserve, removing the bottlenecks of traditional banking.',
    },
    {
      title: 'Ironclad Security & Trust',
      desc: 'CBN-regulated microfinance bank with world-class encryption protecting your peace of mind.',
    },
    {
      title: 'Obsessed with Your Success',
      desc: 'Not just a helpdesk; we are your dedicated financial partners, available whenever you need us.',
    },
  ],
  faqs: {
    personal: [
      {
        question: 'How do I open a WayaBank Personal Account?',
        answer:
          'Download the WayaBank app from Play Store or App Store, use your Phone Number and BVN to register...',
      },
      {
        question: 'Is there a maintenance fee for my account?',
        answer:
          'No, WayaBank personal accounts come with zero monthly maintenance fees.',
      },
      {
        question: 'Can I use WayaBank for international transfers?',
        answer:
          'Currently, WayaBank focuses on seamless local transfers within Nigeria.',
      },
      {
        question: 'How secure is my money with WayaBank?',
        answer:
          'Your security is our top priority. WayaBank is licensed by the CBN, insured by the NDIC...',
      },
      {
        question: 'How do I reset my transaction PIN?',
        answer:
          "You can reset your PIN within the 'Security' section of the WayaBank app.",
      },
    ],
    business: [
      {
        question: 'What is WayaQuick for Merchants?',
        answer:
          'WayaQuick is our dedicated solution for businesses to accept payments effortlessly...',
      },
      {
        question: 'How do I get a WayaPos terminal for my shop?',
        answer:
          'Apply for a WayaPos terminal directly through the Merchant section in the WayaBank app...',
      },
      {
        question: 'What are the requirements for a Business Account?',
        answer:
          'CAC registration documents, TIN, valid IDs of directors, and proof of business address.',
      },
      {
        question: 'Does WayaBank offer business loans?',
        answer:
          'Yes, we offer flexible credit lines and working capital loans to active merchants...',
      },
      {
        question: 'Can I manage multiple business locations on one dashboard?',
        answer:
          'Yes, our Merchant Dashboard allows you to track and manage sales across multiple branches.',
      },
    ],
  },
  testimonials: [
    {
      id: 't1',
      name: 'Elera Obarijima',
      source: 'App Store',
      text: 'Great app with a clean interface and smooth transactions.',
    },
    {
      id: 't2',
      name: 'Usman John',
      source: 'Play Store',
      text: 'Wayabank are the best online banking app, very seamless.',
    },
    {
      id: 't3',
      name: 'Damiiiiidaniel',
      source: 'App Store',
      text: 'Smooth transfers and no complications so far.',
    },
    {
      id: 't4',
      name: 'jgkcd',
      source: 'App Store',
      text: 'Fast transactions and easy navigation.',
    },
    {
      id: 't5',
      name: 'Mackd45',
      source: 'App Store',
      text: 'Amazing social community with a well structured interface.',
    },
    {
      id: 't6',
      name: 'June Johnson',
      source: 'Play Store',
      text: 'I find it very easy to use.',
    },
  ],
  partners: [
    { name: 'CBN', type: 'regulator', image_url: CbnLogo },
    { name: 'NDIC', type: 'regulator', image_url: NdicLogo },
    { name: 'Mastercard', type: 'scheme', image_url: MastercardLogo },
    { name: 'Visa', type: 'scheme', image_url: VisaLogo },
    {
      name: 'Verve',
      type: 'scheme',
      image_url: VerveLogo,
    },
    {
      name: 'Interswitch',
      type: 'scheme',
      image_url: InterswitchLogo,
    },
    {
      name: 'NIBSS',
      type: 'scheme',
      image_url: NibbsLogo,
    },
  ],
  ecosystem: [
    {
      title: 'WayaGram',
      description:
        'Discover beautiful places, people, conversations, vendors and clients.',
      buttonText: 'Explore',
      type: 'gram',
      image_url: WayaGramIcon,
    },
    {
      title: 'WayaPay',
      description:
        'Accept payment on your website or mobile application via card, USSD, bank, wallet.',
      buttonText: 'Explore',
      type: 'pay',
      image_url: WayaPayIcon,
    },
    {
      title: 'WayaPos',
      description:
        'Accept MasterCard, VISA and Verve cards at your physical store.',
      buttonText: 'Explore',
      type: 'pos',
      image_url: WayaPosIcon,
    },
  ],
  ctaSections: {
    support: {
      title: "Have an issue? We're here to help.",
      subline:
        'Our dedicated support team is available 24/7 to assist you with any questions.',
    },
    about: {
      title: 'Join the Financial Revolution',
      subline:
        "The future of banking isn't coming; it's already here. Stop settling for financial institutions that slow you down.",
    },
  },
  features: [
    { title: 'Send Money', body: 'Instant transfers to any Nigerian bank.' },
    {
      title: 'Receive Money',
      body: 'Get paid effortlessly via phone or email.',
    },
    { title: 'Withdraw', body: 'Access your cash whenever you need it.' },
    { title: 'Airtime & Data', body: 'Top up your mobile instantly.' },
    {
      title: 'Utility Bills',
      body: 'Pay electricity and other bills seamlessly.',
    },
    { title: 'Others', body: 'Fund, Pay, and perform other transactions.' },
  ],
  admins: [
    {
      id: 'a1',
      fullName: 'Super Admin',
      email: 'admin@wayapaychat.com',
      password: 'admin@wayapaychat.com',
      phoneNumber: '2340000000000',
      isProtected: true
    },
    {
      id: 'a2',
      fullName: 'Samuel Muoto',
      email: 'samuel.muoto@wayapaychat.com',
      password: 'admin123',
      phoneNumber: '2341234567890',
      isProtected: true
    }
  ],
  howItWorks: {
    steps: [
      {
        title: 'Fill in your details and verify your phone number',
        description:
          'Provide your email and phone number and verify your phone number with an OTP.',
        image_url: Step1Img,
      },
      {
        title: 'Get verified',
        description:
          'Select username & password. Provide your BVN and KYC details and start face & ID verification.',
        image_url: Step2Img,
      },
      {
        title: 'Fund your accounts',
        description:
          'Click on "add money" to find your account details, and top up to start making transfers',
        image_url: Step3Img,
      },
      {
        title: "You're done!",
        description:
          'Click on the Card menu to order a card and get it in 48hrs.',
        image_url: Step4Img,
      },
    ],
  },
  blogs: [
    {
      id: 3,
      title: 'Empowering Small Businesses with WayaPos',
      author: 'Tolu Adeyemi',
      authorRole: 'Head of Merchant Products',
      authorAvatar: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&w=100&q=80',
      date: 'April 8, 2024',
      category: 'Business',
      tags: ['POS', 'small business', 'merchants', 'payments'],
      isFeatured: true,
      readTime: '4 min read',
      views: 3200,
      likes: 217,
      excerpt: 'Learn how our easy-to-use POS terminals are helping merchants scale faster and accept payments effortlessly.',
      featuredImage: 'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&w=800&q=80',
      sections: [
        {
          title: 'Accept Payments Anywhere',
          text: "<p>Whether you're running a boutique in Lagos or a kiosk in Kano, WayaPos ensures you never miss a sale. Our terminals support card payments, bank transfers, QR codes, and USSD — covering every type of customer, every time.</p>",
          image: 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?auto=format&fit=crop&w=800&q=80'
        }
      ]
    },
    {
      id: 1,
      title: 'The Future of Digital Banking in Nigeria',
      author: 'Samuel Muoto',
      authorRole: 'CEO, WayaBank',
      authorAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=100&q=80',
      date: 'April 10, 2024',
      category: 'Banking',
      tags: ['fintech', 'Nigeria', 'digital banking', 'innovation'],
      isFeatured: true,
      readTime: '5 min read',
      views: 4821,
      likes: 312,
      excerpt: 'Explore how WayaBank is leading the charge in reshaping the financial landscape for millions of Nigerians.',
      featuredImage: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=800&q=80',
      sections: [
        {
          title: 'Accessibility for Everyone',
          text: '<p>In a world that moves faster every day, access to financial services shouldn\'t be a bottleneck.</p>',
          image: 'https://images.unsplash.com/photo-1556742049-04ff4361ccb6?auto=format&fit=crop&w=800&q=80',
        }
      ]
    },
    {
      id: 2,
      title: 'Securing Your Digital Assets',
      author: 'Waya Security Team',
      authorRole: 'Cybersecurity Unit',
      authorAvatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=100&q=80',
      date: 'April 5, 2024',
      category: 'Security',
      tags: ['security', 'tips', 'digital safety'],
      isFeatured: false,
      readTime: '3 min read',
      views: 1200,
      likes: 85,
      excerpt: 'Learn the best practices for keeping your bank accounts and digital wallets safe from cyber threats.',
      featuredImage: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=800&q=80',
      sections: [{ title: 'Best Practices', text: '<p>Always use two-factor authentication.</p>' }]
    },
    {
        id: 12,
        title: 'Why Your Business Needs a QR Code Today',
        author: 'Seun Afolabi',
        authorRole: 'Head of Payments Innovation',
        authorAvatar:
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=100&q=80',
        date: 'Feb 10, 2024',
        category: 'Business',
        tags: ['QR code', 'payments', 'contactless', 'business'],
        isFeatured: false,
        readTime: '3 min read',
        views: 2300,
        likes: 156,
        excerpt:
          'Stop missing sales and start accepting instant, contactless payments with a WayaBank QR code.',
        featuredImage:
          'https://images.unsplash.com/photo-1595079676339-1534801ad6cf?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'One Code, Every Payment Method',
            text: '<p>A WayaBank QR code is a universal payment receiver. Whether your customer uses WayaBank, another Nigerian bank app, or even a foreign wallet — one scan routes the payment directly to your account. No hardware. No card reader. Just print, display, and collect.</p>',
            image:
              'https://images.unsplash.com/photo-1595079676339-1534801ad6cf?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'Generate Yours in 30 Seconds',
            text: '<p>Open the WayaBank merchant app, navigate to <em>Accept Payment → QR Code</em>, and your unique code is ready to download, print, or share digitally. You can generate static codes for physical display or dynamic codes with pre-set amounts for faster checkout at busy counters.</p>',
            image:
              'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1595079676339-1534801ad6cf?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 13,
        title: 'Savings Challenges to Try This Quarter',
        author: 'Blessing Uchenna',
        authorRole: 'Head of Growth & Engagement',
        authorAvatar:
          'https://images.unsplash.com/photo-1499887142886-791eca5918cd?auto=format&fit=crop&w=100&q=80',
        date: 'Feb 5, 2024',
        category: 'Banking',
        tags: ['savings', 'challenges', 'goals', 'personal finance'],
        isFeatured: false,
        readTime: '5 min read',
        views: 3700,
        likes: 299,
        excerpt:
          'Fun, interactive savings challenges that help you save more — without feeling the pinch.',
        featuredImage:
          'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'The ₦100 Daily Challenge',
            text: "<p>Start small. Save ₦100 on day one, ₦200 on day two, and increase by ₦100 each day. By day 30, you've accumulated over ₦46,000 — from what felt like nothing. WayaBank's SmartSave can automate these daily increments so you never have to remember to do it manually.</p>",
            image:
              'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'The No-Spend Weekend',
            text: '<p>Pick one weekend per month and commit to zero discretionary spending. No eating out, no impulse buys, no entertainment subscriptions. Transfer whatever you would have spent into your WayaBank savings vault. Most participants save ₦15,000–₦30,000 per no-spend weekend.</p>',
            image:
              'https://images.unsplash.com/photo-1554224155-1696413565d3?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'The Round-Up Hack',
            text: "<p>Enable WayaBank's Round-Up feature, and every transaction you make gets rounded up to the nearest ₦100 — the difference goes straight to savings. Spend ₦2,340 on groceries? ₦60 goes to your vault. Over a month of regular spending, this quietly adds up to thousands.</p>",
            image:
              'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1554224155-1696413565d3?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 14,
        title: 'The Tech Behind Our Zero-Downtime Guarantee',
        author: 'Babatunde Lawal',
        authorRole: 'VP of Engineering',
        authorAvatar:
          'https://images.unsplash.com/photo-1531891437562-4301cf35b7e4?auto=format&fit=crop&w=100&q=80',
        date: 'Feb 1, 2024',
        category: 'Tech',
        tags: ['engineering', 'infrastructure', 'uptime', 'reliability'],
        isFeatured: true,
        readTime: '6 min read',
        views: 2100,
        likes: 178,
        excerpt:
          'Building resilient, fault-tolerant systems to ensure WayaBank is available 24/7, 365 days a year.',
        featuredImage:
          'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'Why Uptime Is Non-Negotiable',
            text: "<p>When you're sending school fees at 11 PM or a vendor needs payment before a shipment leaves at 6 AM, there is no acceptable downtime window. WayaBank processes over 4 million transactions daily — each one a real person depending on our infrastructure to perform flawlessly.</p>",
            image:
              'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'Our Multi-Region Architecture',
            text: "<p>WayaBank's backend runs across three geographically distributed data centres in Nigeria, with automatic failover activated in under 200 milliseconds if one region experiences an outage. Traffic is load-balanced continuously, and each data centre can independently serve 100% of our user base if needed.</p>",
            image:
              'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'Zero-Downtime Deployments',
            text: '<p>Our engineering team ships code updates dozens of times per week using blue-green deployment strategies — meaning new versions are gradually rolled out alongside the existing production environment, with instant rollback capability if any issues are detected. You never experience an update; you just experience improvements.</p>',
            image:
              'https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 15,
        title: "WayaBank Roadmap: What's Coming in Q3 2024",
        author: 'Deji Olatunde',
        authorRole: 'Chief Product Officer',
        authorAvatar:
          'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=100&q=80',
        date: 'Jan 25, 2024',
        category: 'News',
        tags: ['roadmap', 'product', 'upcoming features', '2024'],
        isFeatured: true,
        readTime: '4 min read',
        views: 7800,
        likes: 612,
        excerpt:
          'New features, geographic expansion, and ecosystem upgrades planned for the second half of 2024.',
        featuredImage:
          'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: "What We're Building",
            text: "<p>Q3 2024 is our most ambitious quarter yet. Based on feedback from over 500,000 active users and our merchant community, we've prioritised three major workstreams: investment products, cross-border payments, and an enhanced business banking suite for SMEs.</p>",
            image:
              'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'WayaInvest: Grow Your Money',
            text: '<p>Coming in August, WayaInvest will allow users to invest in Nigerian treasury bills, money market funds, and curated stock portfolios — starting from as little as ₦1,000. Risk ratings, historical performance, and automatic reinvestment options will be built in from day one.</p>',
            image:
              'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'Pan-African Expansion',
            text: "<p>Ghana, Kenya, and Côte d'Ivoire are confirmed for Q3 launch. Users in these countries will be able to open WayaBank accounts, and Nigerian users will be able to send money across borders with zero fees for the first six months.</p>",
            image:
              'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=600&q=80',
        ],
    },

      {
        id: 16,
        title: 'Merchant Story: Scaling from Zero to Hero',
        author: 'Chiamaka Obi',
        authorRole: 'Storyteller & Brand Writer',
        authorAvatar:
          'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=100&q=80',
        date: 'Jan 20, 2024',
        category: 'Business',
        tags: ['merchant story', 'success', 'WayaPos', 'Lagos'],
        isFeatured: false,
        readTime: '8 min read',
        views: 5900,
        likes: 487,
        excerpt:
          "How Adunni's Lagos boutique went from barely breaking even to tripling revenue in six months with WayaPos.",
        featuredImage:
          'https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'The Problem: Losing Sales Every Day',
            text: "<p>Adunni Coker opened her fashion boutique in Surulere in early 2022 with high hopes and a full rack of carefully sourced Ankara pieces. But every time a customer reached for their phone to transfer money, the process was painful — wrong account numbers, failed transactions, long waits for confirmations. Customers lost patience. Sales were lost.</p><p>'I was losing at least three sales every day,' Adunni told us. 'It was frustrating because I knew people wanted to buy. The payment process was chasing them away.'</p>",
            image:
              'https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'The Turn: A WayaPos Terminal',
            text: '<p>A friend introduced Adunni to WayaPos in June 2023. Within a week of setting it up, she noticed an immediate change. Checkouts that used to take five minutes now took under 30 seconds. Customers who previously walked away could now tap and go. The terminal also gave her a digital receipt system, which her corporate clients appreciated for expense tracking.</p>',
            image:
              'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'The Results: Numbers That Speak',
            text: "<p>By December 2023, Adunni's monthly revenue had grown from ₦480,000 to ₦1.4 million — a 192% increase. She opened a second outlet in Lekki Phase 1 and now employs four full-time staff. Her WayaBank merchant dashboard tells her exactly which products move fastest, which days are busiest, and which customers are her most loyal.</p><p>'WayaBank didn't just give me a POS machine,' she says. 'It gave me a business partner.'</p>",
            image:
              'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 18,
        title: 'The Power of Instant Settlements',
        author: 'Tolu Adeyemi',
        authorRole: 'Head of Merchant Products',
        authorAvatar:
          'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&w=100&q=80',
        date: 'Jan 10, 2024',
        category: 'Banking',
        tags: ['settlements', 'liquidity', 'merchants', 'business'],
        isFeatured: false,
        readTime: '4 min read',
        views: 2900,
        likes: 184,
        excerpt:
          "Why waiting T+1 for your money is a thing of the past. How WayaBank's instant settlement engine powers business growth.",
        featuredImage:
          'https://images.unsplash.com/photo-1518458028785-8fbcd101ebb9?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'Liquidity Is the Lifeblood of Business',
            text: "<p>For a small business owner, cash flow is everything. If you sell out of inventory on a Friday afternoon but your bank doesn't settle your funds until Monday morning, you miss out on an entire weekend of potential sales because you cannot restock. You are effectively giving your bank an interest-free loan with your own revenue.</p>",
            image:
              'https://images.unsplash.com/photo-1518458028785-8fbcd101ebb9?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'Real-Time Means Real-Time',
            text: "<p>WayaBank's settlement engine doesn't wait for end-of-day batch processing. The second a customer's payment is confirmed — whether via POS, QR, or transfer — the funds are cleared and available in your WayaBank merchant account. You can use that money immediately to pay staff, settle with vendors, or buy new stock.</p>",
            image:
              'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1518458028785-8fbcd101ebb9?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 19,
        title: 'Building a Tech Career in Nigerian Fintech',
        author: 'Rotimi Fashola',
        authorRole: 'Chief Technology Officer',
        authorAvatar:
          'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=100&q=80',
        date: 'Jan 5, 2024',
        category: 'Tech',
        tags: ['career', 'engineering', 'fintech', 'mentorship'],
        isFeatured: false,
        readTime: '7 min read',
        views: 4800,
        likes: 312,
        excerpt:
          'Advice for the next generation of Nigerian developers, designers, and product managers looking to make an impact.',
        featuredImage:
          'https://images.unsplash.com/photo-1522071823991-b99c223a7097?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'The Best Time to Build Is Now',
            text: "<p>Nigeria's tech ecosystem has moved past the 'early adopter' phase. We are now building systems that millions of people rely on for their daily survival. This shift brings immense responsibility, but also unparalleled opportunity for technical growth. At WayaBank, we're not just solving problems; we're building the future of a continent.</p>",
            image:
              'https://images.unsplash.com/photo-1522071823991-b99c223a7097?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'Skills That Matter in 2024',
            text: '<p>Technical proficiency is a given. What sets great fintech engineers apart is domain knowledge. Understanding the ledger, knowing how settlement cycles work, and being obsessed with security are just as important as knowing your way around a React hooks or a Node.js microservice.</p><p>We look for engineers who are as interested in the <em>why</em> as they are in the <em>how</em>.</p>',
            image:
              'https://images.unsplash.com/photo-1516321326639-d724f73fac58?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1522071823991-b99c223a7097?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1516321326639-d724f73fac58?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 20,
        title: 'WayaGram: More Than Just a Social App',
        author: 'Funmi Balogun',
        authorRole: 'Product Manager, WayaGram',
        authorAvatar:
          'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?auto=format&fit=crop&w=100&q=80',
        date: 'Jan 1, 2024',
        category: 'Lifestyle',
        tags: ['WayaGram', 'social banking', 'community', 'features'],
        isFeatured: false,
        readTime: '4 min read',
        views: 5200,
        likes: 398,
        excerpt:
          'Exploring the advanced social features that make WayaGram the ultimate tool for community-driven banking.',
        featuredImage:
          'https://images.unsplash.com/photo-1516251193007-45ef944ab0c6?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'Community Is at Our Core',
            text: "<p>WayaGram was built on the insight that money is inherently social. We borrow from friends, we split bills with colleagues, we donate to causes we believe in. By bringing these conversations and transactions into the same interface, we've removed the silos that made banking feel cold and impersonal.</p>",
            image:
              'https://images.unsplash.com/photo-1516251193007-45ef944ab0c6?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'WayaGram for Creators',
            text: '<p>Are you a creator? WayaGram allows you to set up exclusive community groups where fans can support your work through monthly subscriptions or one-time tips — with 95% of the revenue going directly into your WayaBank wallet. No complex payout cycles, no intermediary fees.</p>',
            image:
              'https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1516251193007-45ef944ab0c6?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 21,
        title: 'WayaBank Wins Best New Fintech Award 2023',
        author: 'Tunde Oyelaran',
        authorRole: 'Head of Public Relations',
        authorAvatar:
          'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=100&q=80',
        date: 'Dec 20, 2023',
        category: 'News',
        tags: ['award', 'recognition', 'fintech', 'milestone'],
        isFeatured: true,
        readTime: '3 min read',
        views: 8900,
        likes: 723,
        excerpt:
          "WayaBank has been named Best New Fintech of 2023 at the Nigeria Fintech Awards. Here's what this milestone means to us and to you.",
        featuredImage:
          'https://images.unsplash.com/photo-1531053326607-9d349096d887?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'A Year That Changed Everything',
            text: '<p>When WayaBank launched in Q1 2023, we set out to build a bank that Nigerians would love, trust, and recommend. Twelve months later, with 2.3 million registered users, 12,000 active agents, and a Net Promoter Score of 72, we were honoured to be named <strong>Best New Fintech of 2023</strong> at the Nigeria Fintech Awards in Lagos.</p>',
            image:
              'https://images.unsplash.com/photo-1531053326607-9d349096d887?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'This Award Belongs to You',
            text: "<p>Behind every metric is a real person — a trader who received her settlement on time, a student who sent tuition fees from his phone at midnight, a family that saved for a medical emergency using SafeVault. This award is recognition of your trust. We don't take it lightly, and we're committed to earning it every single day in 2024.</p>",
            image:
              'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1531053326607-9d349096d887?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 22,
        title: 'Managing Payroll for Small Teams Without the Headache',
        author: 'Ike Duru',
        authorRole: 'Business Solutions Manager',
        authorAvatar:
          'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=100&q=80',
        date: 'Dec 15, 2023',
        category: 'Business',
        tags: ['payroll', 'HR', 'automation', 'small business'],
        isFeatured: false,
        readTime: '4 min read',
        views: 2600,
        likes: 174,
        excerpt:
          'Automate employee payments, avoid calculation errors, and ensure your team is always paid on time — with WayaBank Payroll.',
        featuredImage:
          'https://images.unsplash.com/photo-1454165833767-027ffea9e77b?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'The Manual Payroll Problem',
            text: '<p>Many small Nigerian businesses still process payroll manually — calculating taxes, pension deductions, and net pay in spreadsheets, then manually initiating individual transfers. This process takes hours, is prone to calculation errors, and creates regulatory exposure if tax deductions are incorrectly computed.</p>',
            image:
              'https://images.unsplash.com/photo-1454165833767-027ffea9e77b?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'WayaBank Payroll: Set Once, Run Forever',
            text: "<p>Configure your team structure, set salaries, and define deduction rules once. On payday — or any day you choose — WayaBank Payroll calculates each employee's net pay, applies PAYE and pension deductions, and executes all transfers simultaneously with a single approval tap.</p><p>Payslips are automatically emailed to each employee. Tax computation reports are available for your accountant in one click.</p>",
            image:
              'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1454165833767-027ffea9e77b?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 23,
        title: 'Is Cash Finally Dead in Nigeria?',
        author: 'Kola Akinwale',
        authorRole: 'Macroeconomics Correspondent',
        authorAvatar:
          'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=100&q=80',
        date: 'Dec 10, 2023',
        category: 'News',
        tags: ['cashless Nigeria', 'CBN', 'digital payments', 'economy'],
        isFeatured: false,
        readTime: '7 min read',
        views: 6100,
        likes: 489,
        excerpt:
          "Analysing Nigeria's rapid transition toward a cashless society, what the data shows, and the role WayaBank is playing.",
        featuredImage:
          'https://images.unsplash.com/photo-1518183214770-9cff984c7e94?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'The Numbers Tell the Story',
            text: "<p>In 2021, Nigeria processed approximately 1.8 billion digital transactions. By 2023, that figure had jumped to over 7 billion — a nearly 4x increase in just two years. The proportion of retail transactions conducted via digital channels rose from 37% to 62% over the same period.</p><p>Cash hasn't disappeared, but it's increasingly becoming the exception rather than the rule.</p>",
            image:
              'https://images.unsplash.com/photo-1518183214770-9cff984c7e94?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'The Naira Redesign That Accelerated Everything',
            text: "<p>The CBN's 2023 naira redesign, while controversial, had an unintended consequence: it pushed millions of Nigerians who had never used digital banking into apps and USSD banking out of necessity. Many never went back to cash. The crisis became a catalyst for inclusion.</p>",
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'What This Means for WayaBank',
            text: "<p>WayaBank's transaction volume grew 340% year-over-year in 2023. Our agent network onboarded 8,000 new merchants in Q4 alone. As Nigeria's cashless economy matures, the opportunity to serve the next 40 million unbanked Nigerians has never been more real — or more urgent.</p>",
            image:
              'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1518183214770-9cff984c7e94?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 24,
        title: 'Travel Banking: Managing Your Money on the Go',
        author: 'Ife Olawale',
        authorRole: 'Lifestyle & Travel Writer',
        authorAvatar:
          'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=100&q=80',
        date: 'Dec 5, 2023',
        category: 'Lifestyle',
        tags: ['travel', 'Africa', 'international payments', 'currency'],
        isFeatured: false,
        readTime: '4 min read',
        views: 3000,
        likes: 222,
        excerpt:
          'How to manage your money seamlessly while touring Africa — with tips on using WayaBank across borders.',
        featuredImage:
          'https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: "Banking Shouldn't Stop at the Border",
            text: "<p>Whether you're on a business trip to Accra, a family holiday in Cape Town, or backpacking through East Africa, access to your money should be effortless. WayaBank's travel features are designed to keep your finances running smoothly no matter which time zone you're in.</p>",
            image:
              'https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'Virtual Dollar Card',
            text: "<p>WayaBank's virtual dollar card, available instantly from the app, is accepted on any platform that supports Visa — from international hotels and airlines to streaming services and e-commerce sites. Top it up in naira and spend in dollars, euros, or pounds at competitive exchange rates.</p>",
            image:
              'https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'Send Money Home in Seconds',
            text: "<p>Need to send naira to family back home while you're travelling? WayaBank's international transfer feature supports remittances from 45 countries with zero fees for the first three transfers per month and competitive mid-market rates thereafter.</p>",
            image:
              'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=600&q=80',
        ],
      },

      {
        id: 25,
        title: 'Data Protection: Our Commitment to Your Privacy',
        author: 'Ngozi Eze',
        authorRole: 'Head of Legal & Compliance',
        authorAvatar:
          'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=100&q=80',
        date: 'Dec 1, 2023',
        category: 'Security',
        tags: ['data protection', 'privacy', 'NDPR', 'compliance'],
        isFeatured: false,
        readTime: '6 min read',
        views: 2400,
        likes: 163,
        excerpt:
          'The strict protocols and policies WayaBank follows to ensure your personal data is never compromised.',
        featuredImage:
          'https://images.unsplash.com/photo-1558494949-ef010cbdcc51?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'Your Data Belongs to You',
            text: '<p>WayaBank collects only the data necessary to operate your account and comply with regulatory requirements. We do not sell your data to third parties, use it for targeted advertising, or share it with affiliates without your explicit consent. Full stop.</p>',
            image:
              'https://images.unsplash.com/photo-1558494949-ef010cbdcc51?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'NDPR Compliance & Beyond',
            text: "<p>WayaBank is fully compliant with Nigeria's Data Protection Regulation (NDPR) and adheres to international best practices aligned with GDPR principles. We conduct annual data protection audits, maintain a dedicated Data Protection Officer, and publish a transparent privacy policy written in plain language — no legal jargon designed to obscure.</p>",
            image:
              'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'How We Protect Your Data Technically',
            text: '<p>All data in transit is encrypted using TLS 1.3. Data at rest is encrypted using AES-256. Access to production systems is restricted to a small number of vetted engineers with multi-factor authentication and comprehensive audit logging. We run quarterly penetration testing with independent third-party security firms.</p>',
            image:
              'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1558494949-ef010cbdcc51?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 26,
        title: 'How WayaBank Is Using AI to Fight Fraud',
        author: 'Rotimi Fashola',
        authorRole: 'Chief Technology Officer',
        authorAvatar:
          'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=100&q=80',
        date: 'Nov 25, 2023',
        category: 'Tech',
        tags: ['AI', 'fraud detection', 'machine learning', 'security'],
        isFeatured: true,
        readTime: '5 min read',
        views: 5300,
        likes: 414,
        excerpt:
          'Inside the machine learning models that analyse millions of transactions per second to protect WayaBank users from fraud.',
        featuredImage:
          'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'The Scale of the Problem',
            text: "<p>Financial fraud in Nigeria costs the economy billions of naira annually. With WayaBank processing over 4 million transactions per day, manually reviewing suspicious activity is impossible. That's where artificial intelligence becomes not just useful — but essential.</p>",
            image:
              'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'How Our AI Fraud Engine Works',
            text: '<p>Every transaction processed by WayaBank is scored by our fraud detection model in real time — typically within 50 milliseconds. The model analyses over 200 features per transaction, including location, device fingerprint, transaction velocity, counterparty history, and behavioural biometrics like typing rhythm and swipe patterns.</p><p>Suspicious transactions are flagged for step-up authentication or blocked outright, while low-risk transactions flow through without friction.</p>',
            image:
              'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'The Results So Far',
            text: "<p>Since deploying our AI fraud engine in Q2 2023, we've reduced fraud losses by 78% while simultaneously reducing false positives — legitimate transactions incorrectly flagged — by 61%. Users experience fewer interruptions; fraudsters experience far more.</p>",
            image:
              'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 27,
        title: 'Introducing WayaKids: Teaching Children About Money',
        author: 'Funmi Balogun',
        authorRole: 'Product Manager, Consumer',
        authorAvatar:
          'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?auto=format&fit=crop&w=100&q=80',
        date: 'Nov 15, 2023',
        category: 'Lifestyle',
        tags: ['financial literacy', 'children', 'education', 'WayaKids'],
        isFeatured: true,
        readTime: '4 min read',
        views: 6800,
        likes: 571,
        excerpt:
          'Our new WayaKids feature helps parents raise financially literate children through gamified savings and spending lessons.',
        featuredImage:
          'https://images.unsplash.com/photo-1516251193007-45ef944ab0c6?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'Why Financial Literacy Starts Young',
            text: '<p>Research consistently shows that financial habits formed before the age of 10 persist into adulthood. Yet financial education is largely absent from Nigerian school curricula, leaving parents to bridge the gap — often without the right tools.</p><p>WayaKids changes that.</p>',
            image:
              'https://images.unsplash.com/photo-1516251193007-45ef944ab0c6?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'How WayaKids Works',
            text: '<p>Parents create a supervised sub-account for their child within the WayaBank app. Children receive a colourful, age-appropriate interface where they can track their allowance, set savings goals with animated progress trackers, and earn virtual badges for hitting milestones.</p><p>Every transaction is visible to the parent in real time, with spending category breakdowns and the ability to set category limits.</p>',
            image:
              'https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'Pocket Money, Reimagined',
            text: '<p>Instead of handing children physical cash that disappears without accountability, parents can send allowances digitally to the WayaKids wallet. Children learn to budget, save, and make deliberate spending choices — all within a safe, parent-monitored environment.</p>',
            image:
              'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1516251193007-45ef944ab0c6?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 28,
        title: "The Nigerian Entrepreneur's Guide to Business Banking",
        author: 'Kolade Sanni',
        authorRole: 'Merchant Success Lead',
        authorAvatar:
          'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=100&q=80',
        date: 'Nov 5, 2023',
        category: 'Business',
        tags: ['business banking', 'SME', 'entrepreneur', 'Nigeria'],
        isFeatured: false,
        readTime: '7 min read',
        views: 4400,
        likes: 356,
        excerpt:
          'A comprehensive guide to setting up and managing business banking with WayaBank — from registration to growth.',
        featuredImage:
          'https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'Why Separate Business and Personal Finances',
            text: '<p>Mixing personal and business finances is one of the most common — and costly — mistakes small business owners make. It clouds your understanding of profitability, creates tax compliance issues, and makes it nearly impossible to access business loans, which require clean financial records.</p><p>Opening a dedicated WayaBank business account takes under five minutes and costs nothing.</p>',
            image:
              'https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'What the WayaBank Business Account Includes',
            text: '<ul><li>Unlimited free transfers between WayaBank accounts</li><li>Discounted rates on inter-bank transfers above ₦50,000</li><li>Integrated invoicing and payment tracking</li><li>Up to 5 sub-accounts for different revenue streams</li><li>Access to working capital loans from ₦100,000</li><li>Dedicated business relationship manager for accounts over ₦5M monthly turnover</li></ul>',
            image:
              'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'Getting Your Business Loan-Ready',
            text: "<p>WayaBank's lending algorithm is based on your actual transaction history — not just your credit score. Businesses that maintain consistent inflows, avoid large unexplained withdrawals, and hold a minimum average monthly balance of ₦200,000 for three consecutive months become eligible for instant working capital loans at competitive rates.</p>",
            image:
              'https://images.unsplash.com/photo-1554224155-1696413565d3?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1554224155-1696413565d3?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 29,
        title: 'USSD Banking: Reaching the Unconnected',
        author: 'Amaka Osuji',
        authorRole: 'CSR & Community Lead',
        authorAvatar:
          'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=100&q=80',
        date: 'Oct 25, 2023',
        category: 'Banking',
        tags: ['USSD', 'financial inclusion', 'feature phone', 'accessibility'],
        isFeatured: false,
        readTime: '4 min read',
        views: 2200,
        likes: 141,
        excerpt:
          "How WayaBank's USSD platform brings full banking functionality to Nigerians without smartphones or internet access.",
        featuredImage:
          'https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: 'Banking Without the Internet',
            text: "<p>Not everyone in Nigeria has a smartphone or reliable data connectivity. For the millions who rely on feature phones or live in areas with poor network coverage, USSD banking is not a backup — it's the primary channel.</p><p>WayaBank's USSD code, <strong>*945#</strong>, gives access to the same core banking features as the mobile app — through any phone, on any network, without data.</p>",
            image:
              'https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'What You Can Do on *945#',
            text: "<p>Through our USSD service, customers can check balances, transfer funds, buy airtime, pay bills, open new savings targets, and access mini-statements — all from a simple numbered menu. The service is available 24/7 and works even when your smartphone's battery is dead.</p>",
            image:
              'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=600&q=80',
        ],
      },
      {
        id: 30,
        title: 'Inside WayaBank HQ: A Day in the Life',
        author: 'Chiamaka Obi',
        authorRole: 'Storyteller & Brand Writer',
        authorAvatar:
          'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=100&q=80',
        date: 'Oct 15, 2023',
        category: 'Lifestyle',
        tags: ['culture', 'team', 'behind the scenes', 'WayaBank'],
        isFeatured: false,
        readTime: '6 min read',
        views: 7200,
        likes: 634,
        excerpt:
          "A peek behind the curtain: what it's like to work at one of Nigeria's fastest-growing fintechs.",
        featuredImage:
          'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=80',
        sections: [
          {
            title: '8:30 AM: The Stand-Up',
            text: "<p>The day at WayaBank HQ in Victoria Island begins with energy. By 8:30 AM, product managers, engineers, designers, and operations staff have crowded into the open-plan war room for a 15-minute stand-up. The question is always the same: 'What shipped yesterday? What's blocked today?'</p><p>The culture is fast, accountable, and fiercely customer-centric. Every meeting starts with a real customer story — a thank-you message, a complaint, a suggestion. The customer is never an abstraction here.</p>",
            image:
              'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'The Engineering Floor',
            text: "<p>WayaBank's 60-person engineering team works across three squads: Core Banking, Growth Products, and Platform Infrastructure. Each squad operates with full autonomy over their roadmap, deploying independently without waiting for monolithic release cycles. The result is a product that improves daily, not quarterly.</p>",
            image:
              'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80',
          },
          {
            title: 'The Customer Obsession Room',
            text: "<p>One room at HQ has no computers, no whiteboards — just comfortable chairs, a TV, and a rotating display of customer feedback. Every employee spends at least one hour per week in this room. It's a reminder that every line of code, every policy decision, and every product feature has a human being on the other side.</p>",
            image:
              'https://images.unsplash.com/photo-1531053326607-9d349096d887?auto=format&fit=crop&w=800&q=80',
          },
        ],
        gallery: [
          'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=600&q=80',
          'https://images.unsplash.com/photo-1531053326607-9d349096d887?auto=format&fit=crop&w=600&q=80',
        ],
      },
    ],
  partners: [
    { name: 'CBN', type: 'regulator', image_url: CbnLogo },
    { name: 'NDIC', type: 'regulator', image_url: NdicLogo },
    { name: 'Mastercard', type: 'scheme', image_url: MastercardLogo },
    { name: 'Visa', type: 'scheme', image_url: VisaLogo },
    { name: 'Verve', type: 'scheme', image_url: VerveLogo },
    { name: 'Interswitch', type: 'scheme', image_url: InterswitchLogo },
    { name: 'NIBSS', type: 'scheme', image_url: NibbsLogo },
  ],
  ecosystem: [
    {
      title: 'WayaGram',
      description: 'Discover beautiful places, people, conversations, vendors and clients.',
      buttonText: 'Explore',
      type: 'gram',
      image_url: WayaGramIcon,
    },
    {
      title: 'WayaPay',
      description: 'Accept payment on your website or mobile application via card, USSD, bank, wallet.',
      buttonText: 'Explore',
      type: 'pay',
      image_url: WayaPayIcon,
    },
    {
      title: 'WayaPos',
      description: 'Accept MasterCard, VISA and Verve cards at your physical store.',
      buttonText: 'Explore',
      type: 'pos',
      image_url: WayaPosIcon,
    },
  ],
  advantages: [
    {
      title: 'Built on Superior Technology',
      desc: 'Infrastructure built for 99.9% uptime, ensuring your transactions go through instantly—every single time.',
    },
    {
      title: 'Unlocking Access to Credit',
      desc: 'Fast, fair access to the credit you deserve, removing the bottlenecks of traditional banking.',
    },
    {
      title: 'Ironclad Security & Trust',
      desc: 'CBN-regulated microfinance bank with world-class encryption protecting your peace of mind.',
    },
    {
      title: 'Obsessed with Your Success',
      desc: 'Not just a helpdesk; we are your dedicated financial partners, available whenever you need us.',
    },
  ],
  loading: false,
  };
export const CmsContextProvider = ({ children }) => {
  // const [siteData, setSiteData] = useState({
  //   homeContent: { hero_headline: "", hero_subtext: "", hero_image: "" },
  //   bankingDetails: { personal: {}, business: {} },
  //   faqs: { personal: [], business: [] },
  //   testimonials: [],
  //   partners: [],
  //   ecosystem: [],
  //   blogs: [],
  //   advantages: [],
  //   howItWorks: { steps: [] },
  //   features: [],
  //   aboutContent: [],
  //   ctaSections: {},
  //   settings: {},
  //   loading: true
  // });
  const [siteData, setSiteData] = useState(fallbacks);
  const fetchCmsData = async () => {
    setSiteData((prev) => ({ ...prev, loading: true }));
    try {
      const res = await httpGet(
        '/api/v1/content/landing-page',
        customBaseUrl.contentManagementUrl
      );
      
      let finalData = fallbacks;
      if (res?.status) {
        finalData = { ...res.data };
      }

      // Merge local admins if they exist
      const localAdmins = localStorage.getItem('waya_cms_local_admins');
      if (localAdmins) {
        finalData.admins = JSON.parse(localAdmins);
      }

      setSiteData({ ...finalData, loading: false });
    } catch (err) {
      console.error('CMS Fetch Error:', err);
      // Fallback to local storage even on error
      const localAdmins = localStorage.getItem('waya_cms_local_admins');
      if (localAdmins) {
        setSiteData({ ...fallbacks, admins: JSON.parse(localAdmins), loading: false });
      } else {
        setSiteData({ ...fallbacks, loading: false });
      }
    }
  };

  const saveBlog = async (blogData) => {
    const updatedBlogs = [...(siteData.blogs || [])];
    const index = updatedBlogs.findIndex(b => b.id === blogData.id);
    
    if (index > -1) {
      updatedBlogs[index] = { ...updatedBlogs[index], ...blogData };
    } else {
      updatedBlogs.push({
        ...blogData,
        id: Date.now(),
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        views: 0,
        likes: 0
      });
    }

    await updateSection('blogs', updatedBlogs);
  };

  const deleteBlog = async (blogId) => {
    const updatedBlogs = (siteData.blogs || []).filter(b => b.id !== blogId);
    await updateSection('blogs', updatedBlogs);
  };

  const savePartner = async (partnerData) => {
    const updatedPartners = [...(siteData.partners || [])];
    const index = updatedPartners.findIndex(p => (p.id && p.id === partnerData.id) || (p.name === partnerData.name && !p.id));
    
    if (index > -1) {
      updatedPartners[index] = { ...updatedPartners[index], ...partnerData };
    } else {
      updatedPartners.push({
        ...partnerData,
        id: Date.now(),
      });
    }

    await updateSection('partners', updatedPartners);
  };

  const deletePartner = async (partnerName) => {
    const updatedPartners = (siteData.partners || []).filter(p => p.name !== partnerName && p.id !== partnerName);
    await updateSection('partners', updatedPartners);
  };

  const saveTestimonial = async (testimonialData) => {
    const updatedTestimonials = [...(siteData.testimonials || [])];
    const index = updatedTestimonials.findIndex(t => t.id === testimonialData.id);
    
    if (index > -1) {
      updatedTestimonials[index] = { ...updatedTestimonials[index], ...testimonialData };
    } else {
      updatedTestimonials.push({
        ...testimonialData,
        id: `t${Date.now()}`,
      });
    }

    await updateSection('testimonials', updatedTestimonials);
  };

  const updateSection = async (sectionKey, data) => {
    showLoader();
    
    // For admins section, we also persist to localStorage to ensure login bypass works immediately
    if (sectionKey === 'admins') {
      localStorage.setItem('waya_cms_local_admins', JSON.stringify(data));
      setSiteData(prev => ({ ...prev, admins: data }));
    }

    try {
      const res = await httpPost(
        `/api/v1/content/update/${sectionKey}`,
        data,
        customBaseUrl.contentManagementUrl
      );
      if (res?.status) {
        success(`${sectionKey} updated successfully!`);
        fetchCmsData(); // Refresh data
      } else {
        // If it's admins, we already updated local state, so we don't strictly "fail" the UI
        if (sectionKey === 'admins') {
           success(`${sectionKey} updated locally! (API unavailable)`);
        } else {
           error(res?.message || 'Failed to update content');
        }
      }
    } catch (err) {
      if (sectionKey === 'admins') {
        success(`${sectionKey} updated locally! (API Error)`);
      } else {
        error('An error occurred during update');
      }
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchCmsData();
  }, []);

  return (
    <CmsContext.Provider value={{ siteData, updateSection, fetchCmsData, saveBlog, deleteBlog, savePartner, deletePartner, saveTestimonial }}>
      {children}
    </CmsContext.Provider>
  );
};
